import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import './09-BuyerVisitScheduleStep.css';

const DAYS = [
  { id: 'SATURDAY',  name: 'شنبه' },
  { id: 'SUNDAY',    name: 'یکشنبه' },
  { id: 'MONDAY',    name: 'دوشنبه' },
  { id: 'TUESDAY',   name: 'سه‌شنبه' },
  { id: 'WEDNESDAY', name: 'چهارشنبه' },
  { id: 'THURSDAY',  name: 'پنجشنبه' },
  { id: 'FRIDAY',    name: 'جمعه' },
];

const TIME_SLOTS = [
  { id: '08:00-10:00', label: '۸:۰۰ – ۱۰:۰۰', short: '۸–۱۰' },
  { id: '10:00-12:00', label: '۱۰:۰۰ – ۱۲:۰۰', short: '۱۰–۱۲' },
  { id: '12:00-14:00', label: '۱۲:۰۰ – ۱۴:۰۰', short: '۱۲–۱۴' },
  { id: '14:00-16:00', label: '۱۴:۰۰ – ۱۶:۰۰', short: '۱۴–۱۶' },
  { id: '16:00-18:00', label: '۱۶:۰۰ – ۱۸:۰۰', short: '۱۶–۱۸' },
  { id: '18:00-20:00', label: '۱۸:۰۰ – ۲۰:۰۰', short: '۱۸–۲۰' },
];

const MORNING_IDS   = ['08:00-10:00', '10:00-12:00'];
const AFTERNOON_IDS = ['12:00-14:00', '14:00-16:00'];

const toFarsi = n => n.toString().replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);

const makeSlotId = (dayId, timeId) => `${dayId}-${timeId}`;

const parseSlotId = sid => {
  const dashIdx = sid.indexOf('-');
  return { dayId: sid.substring(0, dashIdx), timeId: sid.substring(dashIdx + 1) };
};

const getSlotsBetween = (startId, endId) => {
  const di1 = DAYS.findIndex(d => d.id === parseSlotId(startId).dayId);
  const ti1 = TIME_SLOTS.findIndex(t => t.id === parseSlotId(startId).timeId);
  const di2 = DAYS.findIndex(d => d.id === parseSlotId(endId).dayId);
  const ti2 = TIME_SLOTS.findIndex(t => t.id === parseSlotId(endId).timeId);
  const minD = Math.min(di1, di2), maxD = Math.max(di1, di2);
  const minT = Math.min(ti1, ti2), maxT = Math.max(ti1, ti2);
  const ids = [];
  for (let d = minD; d <= maxD; d++)
    for (let t = minT; t <= maxT; t++)
      ids.push(makeSlotId(DAYS[d].id, TIME_SLOTS[t].id));
  return ids;
};

function BuyerVisitScheduleStep({ onSubmit, initialData = {}, onPreviousStep }) {
  const [selected, setSelected]           = useState(new Set(initialData.visiting_schedule || []));
  const [disabledSlots, setDisabledSlots] = useState(new Set());
  const [loading, setLoading]             = useState(false);
  const [preview, setPreview]             = useState(new Set());

  // All drag state in refs — no stale closures, no extra renders on mouse-move
  const isDragging  = useRef(false);
  const dragMode    = useRef(null);  // 'select' | 'deselect'
  const dragStartId = useRef(null);
  const dragEndId   = useRef(null);
  // Keep a live mirror of `selected` in a ref so drag commit can read it without
  // being in useCallback's dependency array
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const disabledRef = useRef(disabledSlots);
  useEffect(() => { disabledRef.current = disabledSlots; }, [disabledSlots]);

  // ── API load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiRequest(apiRoutes.visitingSchedules, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDisabledSlots(new Set(data.filter(s => !s.is_active).map(s => s.id)));
        }
      } catch {
        // Non-fatal — all slots stay enabled
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Core drag logic (plain functions — no useCallback needed) ────────────────
  const startDrag = (sid) => {
    if (disabledRef.current.has(sid)) return;
    isDragging.current  = true;
    dragStartId.current = sid;
    dragEndId.current   = sid;
    dragMode.current    = selectedRef.current.has(sid) ? 'deselect' : 'select';
    setPreview(new Set([sid]));
  };

  const moveDrag = (sid) => {
    if (!isDragging.current || disabledRef.current.has(sid)) return;
    dragEndId.current = sid;
    setPreview(new Set(getSlotsBetween(dragStartId.current, sid)));
  };

  const commitDrag = () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const affected = getSlotsBetween(dragStartId.current, dragEndId.current)
      .filter(sid => !disabledRef.current.has(sid));
    const mode = dragMode.current;

    setSelected(prev => {
      const next = new Set(prev);
      affected.forEach(sid => mode === 'select' ? next.add(sid) : next.delete(sid));
      return next;
    });

    setPreview(new Set());
    dragStartId.current = null;
    dragEndId.current   = null;
    dragMode.current    = null;
  };

  // Attach a single mouseup to the document so releasing outside the grid still commits.
  // We use a native listener (not React synthetic) so it always fires after onMouseDown.
  useEffect(() => {
    const onUp = () => commitDrag();
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Touch: resolve element under finger
  const slotUnderTouch = (e) => {
    const touch = e.touches[0] || e.changedTouches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    return el?.dataset?.slot || null;
  };

  // ── Quick-select helpers ─────────────────────────────────────────────────────
  const allActiveIds = DAYS.flatMap(d =>
    TIME_SLOTS.map(t => makeSlotId(d.id, t.id))
  ).filter(sid => !disabledSlots.has(sid));

  const handleSelectAll = () =>
    setSelected(prev => prev.size === allActiveIds.length ? new Set() : new Set(allActiveIds));

  const handleSelectMornings = () =>
    setSelected(prev => {
      const next = new Set(prev);
      DAYS.forEach(d => MORNING_IDS.forEach(t => {
        const sid = makeSlotId(d.id, t);
        if (!disabledSlots.has(sid)) next.add(sid);
      }));
      return next;
    });

  const handleSelectAfternoons = () =>
    setSelected(prev => {
      const next = new Set(prev);
      DAYS.forEach(d => AFTERNOON_IDS.forEach(t => {
        const sid = makeSlotId(d.id, t);
        if (!disabledSlots.has(sid)) next.add(sid);
      }));
      return next;
    });

  const handleClearAll = () => setSelected(new Set());

  const handleToggleDay = (dayId) => {
    const daySlots = TIME_SLOTS
      .map(t => makeSlotId(dayId, t.id))
      .filter(sid => !disabledSlots.has(sid));
    setSelected(prev => {
      const allSel = daySlots.every(sid => prev.has(sid));
      const next = new Set(prev);
      daySlots.forEach(sid => allSel ? next.delete(sid) : next.add(sid));
      return next;
    });
  };

  const handleRemoveChip = (sid) =>
    setSelected(prev => { const next = new Set(prev); next.delete(sid); return next; });

  const handleContinue = () =>
    onSubmit({ visiting_schedule: [...selected], completed: true });

  // ── Derived display values ───────────────────────────────────────────────────
  const isDayFull = (dayId) =>
    TIME_SLOTS.every(t => {
      const sid = makeSlotId(dayId, t.id);
      return disabledSlots.has(sid) || selected.has(sid);
    });

  const cellClass = (sid) => {
    if (disabledSlots.has(sid)) return 'bvs-cell bvs-cell--disabled';
    let cls = 'bvs-cell';
    if (selected.has(sid))         cls += ' bvs-cell--selected';
    else if (preview.has(sid))     cls += dragMode.current === 'deselect'
                                          ? ' bvs-cell--preview-remove'
                                          : ' bvs-cell--preview-add';
    return cls;
  };

  const orderedSelected = DAYS.flatMap(d =>
    TIME_SLOTS.map(t => makeSlotId(d.id, t.id))
  ).filter(sid => selected.has(sid));

  // ── Loading screen ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bvs-wrap">
        <div className="bvs-header">
          <h3 className="bvs-title">برنامه‌ریزی بازدید</h3>
          <p className="bvs-subtitle">در حال بارگذاری زمان‌های قابل بازدید…</p>
        </div>
        <div className="bvs-loading">
          <div className="bvs-spinner" />
          <span>در حال بارگذاری…</span>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bvs-wrap">
      {/* Header */}
      <div className="bvs-header">
        <h3 className="bvs-title">برنامه‌ریزی بازدید</h3>
        <p className="bvs-subtitle">
          روی خانه‌ها کلیک کنید یا بکشید تا زمان‌های مناسب را انتخاب کنید
        </p>
      </div>

      {/* Toolbar */}
      <div className="bvs-toolbar">
        <button type="button" className="bvs-pill" onClick={handleSelectAll}>
          {selected.size === allActiveIds.length ? 'لغو انتخاب همه' : 'انتخاب همه'}
        </button>
        <button type="button" className="bvs-pill" onClick={handleSelectMornings}>صبح‌ها</button>
        <button type="button" className="bvs-pill" onClick={handleSelectAfternoons}>بعدازظهرها</button>
        <button type="button" className="bvs-pill bvs-pill--ghost" onClick={handleClearAll}>پاک کردن</button>
        <span className="bvs-badge">{toFarsi(selected.size)} بازه انتخاب شده</span>
      </div>

      {/* Grid */}
      <div className="bvs-grid-wrap">
        <div className="bvs-grid">
          <div className="bvs-corner" />

          {DAYS.map(day => (
            <div
              key={day.id}
              className={`bvs-day-head${isDayFull(day.id) ? ' bvs-day-head--full' : ''}`}
            >
              <span className="bvs-day-name">{day.name}</span>
              <button
                type="button"
                className="bvs-day-toggle"
                onClick={() => handleToggleDay(day.id)}
                title={isDayFull(day.id) ? 'لغو انتخاب روز' : 'انتخاب همه روز'}
              >
                {isDayFull(day.id) ? '−' : '+'}
              </button>
            </div>
          ))}

          {TIME_SLOTS.map(time => (
            <React.Fragment key={time.id}>
              <div className="bvs-time-label">{time.short}</div>
              {DAYS.map(day => {
                const sid = makeSlotId(day.id, time.id);
                const isDisabled = disabledSlots.has(sid);
                return (
                  <div
                    key={sid}
                    data-slot={sid}
                    className={cellClass(sid)}
                    onMouseDown={e => { e.preventDefault(); startDrag(sid); }}
                    onMouseEnter={() => moveDrag(sid)}
                    onTouchStart={e => { e.preventDefault(); const s = slotUnderTouch(e); if (s) startDrag(s); }}
                    onTouchMove={e => { e.preventDefault(); const s = slotUnderTouch(e); if (s) moveDrag(s); }}
                    onTouchEnd={() => commitDrag()}
                    role="checkbox"
                    aria-checked={selected.has(sid)}
                    aria-label={`${day.name} ${time.label}`}
                    aria-disabled={isDisabled}
                    tabIndex={isDisabled ? -1 : 0}
                    onKeyDown={e => {
                      if ((e.key === ' ' || e.key === 'Enter') && !isDisabled) {
                        e.preventDefault();
                        setSelected(prev => {
                          const next = new Set(prev);
                          next.has(sid) ? next.delete(sid) : next.add(sid);
                          return next;
                        });
                      }
                    }}
                  >
                    {isDisabled && <span className="bvs-cell-disabled-label">غیرفعال</span>}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bvs-legend">
        <span className="bvs-legend-item">
          <span className="bvs-legend-dot bvs-legend-dot--selected" />انتخاب شده
        </span>
        <span className="bvs-legend-item">
          <span className="bvs-legend-dot bvs-legend-dot--preview" />در حال انتخاب
        </span>
        <span className="bvs-legend-item">
          <span className="bvs-legend-dot bvs-legend-dot--disabled" />غیرفعال
        </span>
      </div>

      {/* Summary */}
      {selected.size > 0 ? (
        <div className="bvs-summary">
          <div className="bvs-summary-head">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {toFarsi(selected.size)} بازه زمانی انتخاب شده
          </div>
          <div className="bvs-chips">
            {orderedSelected.map(sid => {
              const { dayId, timeId } = parseSlotId(sid);
              const dayName = DAYS.find(d => d.id === dayId)?.name || dayId;
              const timeLbl = TIME_SLOTS.find(t => t.id === timeId)?.short || timeId;
              return (
                <span key={sid} className="bvs-chip">
                  {dayName} {timeLbl}
                  <button
                    type="button"
                    className="bvs-chip-remove"
                    onClick={() => handleRemoveChip(sid)}
                    aria-label={`حذف ${dayName} ${timeLbl}`}
                  >حذف</button>
                </span>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bvs-summary bvs-summary--empty">
          هیچ زمانی انتخاب نشده — روی خانه‌های بالا کلیک یا بکشید
        </div>
      )}

      {/* Navigation */}
      <div className="bvs-nav">
        <button
          type="button"
          className="bvs-btn bvs-btn--primary"
          onClick={handleContinue}
          disabled={selected.size === 0}
        >
          تأیید و ادامه
        </button>
        <button
          type="button"
          className="bvs-btn bvs-btn--secondary"
          onClick={onPreviousStep}
        >
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default BuyerVisitScheduleStep;
